/* Copyright (c) 2020-present CloudIO Inc */

import { isChildAllowed } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { getName } from '../../registry/Manager';
import { defaultValue } from './Mirror';

const MirrorManager: ComponentManagerFn<'Mirror'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Mirror'>(platform, 'Mirror'),
    type: 'Mirror',
    name: 'Mirror',
    defaultValue,
    props: {
      targetItemId: {
        label: 'Target Item ID',
        type: 'ItemID',
        validateItemId: (
          value,
          { appUid, pageId, itemId, platform: _platform },
        ) => {
          if (value) {
            const page = _platform.getPage(appUid, pageId);
            const { allowed, m } = isChildAllowed({
              page,
              appUid,
              pageId,
              childItemId: value,
              parentItemId: page[itemId].parent,
              platform: _platform,
            });
            if (typeof allowed === 'string') {
              return allowed;
            }
            if (!allowed) {
              return `${
                m.name
              } is not allowed in this mirror item under ${getName(
                page[page[itemId].parent].type,
              )}`;
            }
          }
          return '';
        },
      },
    },
    category: 'Containers',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The Mirror component is a powerful structural tool that allows you to display a live duplicate of an existing component in a different location on the same page. Unlike a standard "Copy/Paste" action which creates a detached clone, the Mirror creates a reference.
This means the mirrored version stays perfectly synchronized with the original. If the logic or content of the original component changes, the Mirror updates instantly. This is essential for complex interfaces where you need the same widget (like a "Filter Panel" or "Cart Summary") to appear in multiple places (e.g., both the sidebar and the header) without duplicating code or maintenance effort.

**1. Configuration**<br/>
The Mirror component is intentionally minimal. It does not possess its own visual properties; strictly acting as a viewport for the target component.
* **Target Item ID:** The unique identifier of the component you wish to duplicate.
* **How to use:** Select the Item ID of an existing component on the current page. The Mirror will immediately render that component's content inside itself.

**2. Placement & Validation**<br/>
While you can mirror almost any component, the system enforces strict layout rules to ensure the page remains valid.
* **Context Validation:** When you select a target, the system runs a check to ensure that the original component type is allowed to exist in the Mirror's current location.
* **Example:** If you place a Mirror inside a Button, you cannot set the target to a Form, because Forms are not allowed inside Buttons. The Mirror ensures you don't accidentally create invalid hierarchies.

**3. Runtime Behavior**<br/>
* **Single Source of Truth:** The Mirror does not maintain its own state. It reflects the state of the original component. User interactions with the Mirror (like typing in a text box) are effectively happening to the original component.
* **Scope:** The Mirror only works for components within the same page. You cannot mirror a component from a completely different page file.`,
  };
};

export default MirrorManager;
